import DataStructure, { sanitize } from "./DataStructure.js";
import { deepFreeze, formatPercentage } from "../util/misc.js";
import { i18n } from "../boot/i18n.js";
import { defaults, isEqual, omit } from "lodash";

export const CUSTOMER_TERMS_FIELDS = {
  type: "",
  cashSale: false,
  commercial: false,
  installation: false,
  // Whether the quote may carry material line items. `false` means these terms
  // are for labor alone and the quote must contain no material; `null` (the
  // default) makes no assertion. Enforced when saving a quote or change order,
  // alongside the `installation` ⇄ labor check.
  material: null,
  downPayment: null,
  discount: null,
  discountDays: null,
  netDays: null,
  financeCharge: null,
  financeDays: null,
};
deepFreeze(CUSTOMER_TERMS_FIELDS);

export const CUSTOMER_TERMS = {
  cashInstalled: {
    label: "terms.cashInstalled.label",
    text: "terms.cashInstalled.text",
    cashSale: true,
    commercial: false,
    installation: true,
    downPayment: 50,
  },
  // Installation sold on its own: the goods being installed were bought
  // elsewhere (third-party items) or on an earlier sale in the project (the
  // cross-sale install flow), so this quote carries no material. With nothing
  // to collect before delivery there is no deposit — the whole balance is
  // collected when the installation is signed off.
  cashInstallOnly: {
    label: "terms.cashInstallOnly.label",
    text: "terms.cashInstallOnly.text",
    cashSale: true,
    commercial: false,
    installation: true,
    material: false,
    downPayment: 0,
  },
  cashMaterialOnly: {
    label: "terms.cashMaterialOnly.label",
    text: "terms.cashMaterialOnly.text",
    cashSale: true,
    commercial: false,
    installation: false,
    downPayment: 50,
  },
  cashPaidInFull: {
    label: "terms.cashPaidInFull.label",
    text: "terms.cashPaidInFull.text",
    cashSale: true,
    commercial: false,
    installation: null,
    downPayment: 100,
  },
  accountInstalled: {
    label: "terms.accountInstalled.label",
    text: "terms.accountInstalled.text",
    cashSale: false,
    commercial: false,
    installation: true,
    discount: 1.5,
    discountDays: 10,
    netDays: 30,
    financeCharge: 2,
    financeDays: 45,
  },
  accountInstalledNoDiscount: {
    label: "terms.accountInstalledNoDiscount.label",
    text: "terms.accountInstalledNoDiscount.text",
    cashSale: false,
    commercial: false,
    installation: true,
    discount: null,
    discountDays: null,
    netDays: 30,
    financeCharge: 2,
    financeDays: 45,
  },
  accountMaterialOnly: {
    label: "terms.accountMaterialOnly.label",
    text: "terms.accountMaterialOnly.text",
    cashSale: false,
    commercial: false,
    installation: false,
    discount: 1.5,
    discountDays: 10,
    netDays: 30,
    financeCharge: 2,
    financeDays: 45,
  },
  accountMaterialOnlyNoDiscount: {
    label: "terms.accountMaterialOnlyNoDiscount.label",
    text: "terms.accountMaterialOnlyNoDiscount.text",
    cashSale: false,
    commercial: false,
    installation: false,
    discount: null,
    discountDays: null,
    netDays: 30,
    financeCharge: 2,
    financeDays: 45,
  },
  commercialInstalled: {
    label: "terms.commercialInstalled.label",
    text: "terms.commercialInstalled.text",
    cashSale: false,
    commercial: true,
    installation: true,
    discount: 1.5,
    discountDays: 10,
    netDays: 30,
    financeCharge: 2,
    financeDays: 45,
  },
  commercialMaterialOnly: {
    label: "terms.commercialMaterialOnly.label",
    text: "terms.commercialMaterialOnly.text",
    cashSale: false,
    commercial: true,
    installation: false,
    discount: 1.5,
    discountDays: 10,
    netDays: 30,
    financeCharge: 2,
    financeDays: 45,
  },
};
deepFreeze(CUSTOMER_TERMS);

// Terms whose deposit is fixed by the terms themselves, so the editor must not
// offer a Down Payment % to override: "Paid in Full" is 100% by definition, and
// "Installation Only" is 0% because there is no material to collect for before
// delivery. Every other cash term negotiates the percentage.
export const FIXED_DOWN_PAYMENT_TERMS = ["cashPaidInFull", "cashInstallOnly"];
deepFreeze(FIXED_DOWN_PAYMENT_TERMS);

function getDefaults(type) {
  return omit(CUSTOMER_TERMS[type], ["label", "text"]);
}

export default class CustomerTerms extends DataStructure {
  constructor(data = {}) {
    super(CustomerTerms.sanitize(data));
  }

  static getDefaults = getDefaults;

  static sanitize(data) {
    if (data && data.type in CUSTOMER_TERMS) {
      // `data` may be a frozen store object; default into a fresh {} so we
      // never mutate the frozen source (lodash defaults mutates its 1st arg).
      data = defaults({}, data, getDefaults(data.type));
      return data;
    } else {
      return sanitize(data, CUSTOMER_TERMS_FIELDS);
    }
  }

  setType(type) {
    type = getDefaults(type);
    Object.assign(this.data, type);
  }

  get isValid() {
    return this.data.type in CUSTOMER_TERMS;
  }

  get isCustom() {
    return (
      this.isValid &&
      !isEqual(this.data, { ...this.data, ...getDefaults(this.data.type) })
    );
  }

  // Formatting

  get type() {
    return CUSTOMER_TERMS[this.data.type];
  }

  get label() {
    return this.isValid
      ? i18n.global.t(this.type.label) +
          (this.isCustom ? ` (${i18n.global.t("Custom")})` : "")
      : "";
  }

  get description() {
    if (this.cashSale) {
      if (this.data.material === false) {
        return i18n.global.t("terms.cashInstallOnly.label");
      }
      return i18n.global.t(
        this.installation
          ? "terms.cashInstalled.label"
          : "terms.cashMaterialOnly.label"
      );
    } else {
      if (this.data.discount && this.data.discountDays) {
        return `${this.discountPercent} ${this.discountDays} Net ${this.netDays}`;
      } else {
        return `Net ${this.netDays}`;
      }
    }
  }

  get downPaymentPercent() {
    return formatPercentage(this.data.downPayment);
  }

  get remainderPercent() {
    return formatPercentage(100 - this.data.downPayment);
  }

  get discountPercent() {
    return formatPercentage(this.data.discount);
  }

  get financeChargeMonthlyPercent() {
    return formatPercentage(this.data.financeCharge);
  }

  get financeChargeAnnualPercent() {
    return formatPercentage(12 * this.data.financeCharge);
  }

  toArray(omitCommon = false) {
    const terms = this.data;
    if (!terms) {
      return [];
    }
    let output = [];
    if (terms.type in CUSTOMER_TERMS) {
      output.push(
        ...i18n.global
          .t(CUSTOMER_TERMS[terms.type].text, {
            downPaymentPercent: this.downPaymentPercent,
            remainderPercent: this.remainderPercent,
            discountPercent: this.discountPercent,
            discountDays: this.discountDays,
            netDays: this.netDays,
            financeChargeMonthlyPercent: this.financeChargeMonthlyPercent,
            financeChargeAnnualPercent: this.financeChargeAnnualPercent,
          })
          .split("\n")
      );
    }
    if (!omitCommon) {
      output.push(...i18n.global.t("terms.allSales").split("\n"));
    }
    return output;
  }

  toString(options = { html: false, omitCommon: false }) {
    const output = this.toArray(options.omitCommon);
    if (options.html) {
      return "<ol><li>" + output.join("</li><li>") + "</li></ol>";
    } else {
      return output.join("\n");
    }
  }
}
